// action types
import { SubscriptionActions } from 'client/actions/actionTypes'
import { getCurrentMonthInvoiceCount } from 'client/actions/helpers/queries'
import { IAvailablePlanNames } from 'client/components/Subscription/PremiumPlans/IPremiumInterface'
import subscriptionKeys from 'client/components/txtkeys/subscriptionKeys'
// typescript
import { ApiError } from 'client/Interfaces/ErrorInterfaces'
import { StateInterface } from 'client/Interfaces/StateInterface'
import {
  DefaultQuotaWhenNoSubscription,
  QuotaTypes,
  SubscriptionInterface,
  SubscriptionInvoiceInterface,
  UsedQuotaInterface,
} from 'client/Interfaces/Subscription'
import {
  CompanySubscriptionStateInterface,
  SubscriptionManagementStateInterface,
} from 'client/Interfaces/Subscription/ReduxStateInterfaces'
// selectors
import { subscriptionManagementSelector } from 'client/selectors/subscription'
import { createSelector } from 'reselect'

// helpers
import { ReducerBuilder } from '../ReducerBuilder'

type payloadType = SubscriptionInterface | ApiError
type invoicePayloadType = SubscriptionInvoiceInterface[] | ApiError
type continueSubscriptionPayload = SubscriptionInterface
type quotaPayloadType = UsedQuotaInterface[]

const initialState = { loaded: false, subscription: null }
const isSubscription = (payload: SubscriptionInterface | ApiError): payload is SubscriptionInterface =>
  (payload as SubscriptionInterface).id !== undefined &&
  (payload as SubscriptionInterface).plan !== undefined &&
  (payload as SubscriptionInterface).payment !== undefined

const setSubscription = (state: CompanySubscriptionStateInterface, payload: payloadType) => {
  return { loaded: true, subscription: payload }
}

const setInvoices = (state: CompanySubscriptionStateInterface, payload: invoicePayloadType) => {
  return { ...state, invoices: payload }
}

const cardDeclined = (state: CompanySubscriptionStateInterface) => {
  return {
    ...state,
    errorMessage: {
      headingKey: subscriptionKeys.paymentDeclinedHeading,
      descriptionKey: subscriptionKeys.paymentDeclinedDescription,
    },
  }
}

const udpatePaymentError = (state: CompanySubscriptionStateInterface, payload) => {
  return {
    ...state,
    updatePaymentSuccessfull: false,
    updatePaymentError: {
      message: payload.message,
    },
  }
}
const cancelFailed = (state: CompanySubscriptionStateInterface) => {
  return {
    ...state,
    errorMessage: {
      headingKey: subscriptionKeys.cancelFailed,
    },
  }
}

const updateQuota = (state: CompanySubscriptionStateInterface, payload: quotaPayloadType) => {
  const { subscription } = state

  return {
    ...state,
    subscription: {
      ...subscription,
      quotas: payload,
    },
  }
}

const clearError = (state: CompanySubscriptionStateInterface) => {
  return Object.assign({}, state, { errorMessage: null })
}

const confirmCancelSubscription = (state: CompanySubscriptionStateInterface) => {
  return { ...state, cancelConfirmationInitiated: true }
}
const removeCancelSubscriptionConfirmation = (state: CompanySubscriptionStateInterface) => {
  return { ...state, cancelConfirmationInitiated: false }
}
const continueCurrentPlan = (state: CompanySubscriptionStateInterface, payload: continueSubscriptionPayload) => {
  return {
    ...state,
    subscription: {
      ...payload,
    },
  }
}
const reducer = ReducerBuilder.create<
  CompanySubscriptionStateInterface,
  continueSubscriptionPayload | payloadType | quotaPayloadType | invoicePayloadType
>(initialState)
  .mapAction(SubscriptionActions.LOAD_SUBSCRIPTION, setSubscription)
  .mapAction(SubscriptionActions.LOAD_INVOICES, setInvoices)
  .mapAction(SubscriptionActions.PAY_SUBSCRIPTION, setSubscription)
  .mapAction(SubscriptionActions.CONTINUE_CURRENT_PLAN, continueCurrentPlan)
  .mapAction(SubscriptionActions.UPDATE_PAYMENT_TOKEN, setSubscription)
  .mapAction(SubscriptionActions.PAY_SUBSCRIPTION, setSubscription)
  .mapAction(SubscriptionActions.UPDATE_PAYMENT_ERROR, udpatePaymentError)
  .mapAction(SubscriptionActions.PAYMENT_DECLINED, cardDeclined)
  .mapAction(SubscriptionActions.CLEAR_ERROR, clearError)
  .mapAction(SubscriptionActions.CANCEL_SUBSCRIPTION, setSubscription)
  .mapAction(SubscriptionActions.CANCEL_FAILED, cancelFailed)
  .mapAction(SubscriptionActions.LOAD_COMPANY_QUOTA, updateQuota)
  .mapAction(SubscriptionActions.CONFIRM_CANCEL_SUBSCRIPTION, confirmCancelSubscription)
  .mapAction(SubscriptionActions.REMOVE_CANCEL_SUBSCRIPTION_CONFIRMATION, removeCancelSubscriptionConfirmation)
  .build()

export default reducer

// Selectors

export const companySubscriptionSelector = createSelector<
  StateInterface,
  SubscriptionManagementStateInterface,
  CompanySubscriptionStateInterface
>(subscriptionManagementSelector, (subscriptionManagement) => subscriptionManagement?.companySubscription)

export const isItAnActiveSubscription = createSelector<StateInterface, CompanySubscriptionStateInterface, boolean>(
  companySubscriptionSelector,
  (companySubscription) => {
    if (!companySubscription?.loaded || !companySubscription.subscription) {
      return false
    }
    const {
      subscription: { status },
    } = companySubscription
    return status !== 'free' && status !== 'no_subscription'
  },
)

export const hasNoSubscription = createSelector<StateInterface, CompanySubscriptionStateInterface, boolean>(
  companySubscriptionSelector,
  (companySubscription) => {
    if (!companySubscription?.loaded || !companySubscription?.subscription) {
      return true
    }
    const {
      subscription: { status },
    } = companySubscription
    return status === 'no_subscription'
  },
)

export const getCompanySubscriptionSelector = createSelector<StateInterface, SubscriptionInterface>(
  companySubscriptionSelector,
  (companySubscription) => {
    return companySubscription?.subscription ? companySubscription?.subscription : null
  },
)

export const getAllQuotasSelector = createSelector<StateInterface, SubscriptionInterface, UsedQuotaInterface[] | null>(
  getCompanySubscriptionSelector,
  (subscription) => {
    return subscription ? subscription?.quotas : null
  },
)

export const getCompanySubscriptionInvoicesSelector = createSelector<
  StateInterface,
  CompanySubscriptionStateInterface,
  SubscriptionInvoiceInterface[]
>(companySubscriptionSelector, (companySubscription) => {
  return companySubscription.invoices ? companySubscription.invoices : null
})

export const getQuotaByTypeSelector = (state, quotaType: QuotaTypes): UsedQuotaInterface => {
  const quotas = getAllQuotasSelector(state)
  return quotas && quotas.find((quota) => quota.type === quotaType)
}

export const getRemainingQuotaSelector = createSelector<StateInterface, QuotaTypes, UsedQuotaInterface, number>(
  getQuotaByTypeSelector,
  (quota) => {
    if (quota) {
      if (quota.total === -1) {
        // Stands for unlimited
        return -1
      } else {
        return quota.total - quota.used
      }
    }
    return 0
  },
)

// Check if plan metadata supports a feature
export const doesCurrentPlanSupportFeature = (
  subscription: SubscriptionInterface,
  featureType: QuotaTypes,
  minQuota: number = 0,
): boolean => {
  const result =
    subscription &&
    subscription.plan &&
    subscription.plan.quotas.some(({ name, quota }) => {
      return name === featureType && (quota > minQuota || quota === -1)
    })
  return Boolean(result)
}

export interface QuotaInfoInterface {
  isQuotaAvailable: boolean
  used: number
  total: number
}
// Check if plan metadata has enough quota to support this feature
// This is to check for features whose consumedQuota is not saved by the backend
// isQuotaAvailable evaluates if additional quota is available
export const getQuotaInfo = (state: StateInterface, featureType: QuotaTypes): QuotaInfoInterface => {
  let used = 0
  let total = 0
  const quota = getQuotaByTypeSelector(state, featureType)
  if (featureType === QuotaTypes.CUSTOMERS) {
    used = state.customers && state.customers.loaded ? state.customers.collection.length : 0
    total = DefaultQuotaWhenNoSubscription.CUSTOMERS
    if (total > used && !quota) {
      // !quota check is to skip if there is subscription
      return {
        isQuotaAvailable: true,
        used,
        total,
      }
    }
  }
  const remainingQuota = getRemainingQuotaSelector(state, featureType)
  if (quota) {
    used = quota.used
    total = quota.total
  }
  return {
    isQuotaAvailable: remainingQuota > 0 || (remainingQuota === -1 && total === -1),
    used,
    total,
  }
}

// Same as above, but handles an async call to calculate invoices
export const getQuotaInfoAsync = async (
  state: StateInterface,
  featureType: QuotaTypes,
): Promise<QuotaInfoInterface> => {
  if (featureType === QuotaTypes.FREE_INVOICE_LIMIT) {
    const plan = state.subscriptionManagement.companySubscription.subscription.plan
    const used = await getCurrentMonthInvoiceCount(state.subscriptionManagement.settings.settings?.oldSubEndDate)
    const total = DefaultQuotaWhenNoSubscription.FREE_INVOICE_LIMIT
    if (plan.name === IAvailablePlanNames['plan.none'] || plan.name === IAvailablePlanNames['plan.free']) {
      if (total > used) {
        return {
          isQuotaAvailable: true,
          used,
          total,
        }
      }

      return {
        isQuotaAvailable: false,
        used,
        total,
      }
    }
    return {
      isQuotaAvailable: true,
      used,
      total: -1,
    }
  }

  return getQuotaInfo(state, featureType)
}

// Returns whether a customer can be added, or if it needs upgrade or subscribe
export const getCustomerAddingAction = (state: StateInterface): 'canAdd' | 'canUpgrade' | 'canSubscribe' => {
  // eslint-disable-next-line no-nested-ternary
  return !state.settings.isOneProductUser || getQuotaInfo(state, QuotaTypes.CUSTOMERS).isQuotaAvailable
    ? 'canAdd'
    : doesCurrentPlanSupportFeature(state.subscriptionManagement.companySubscription.subscription, QuotaTypes.CUSTOMERS)
      ? 'canUpgrade'
      : 'canSubscribe'
}

export const hasDueOrDisputedConsumables = (subscription: SubscriptionInterface) => {
  const dueInvoices = subscription?.payment?.dueInvoices
  const disputeInvoices = subscription?.payment?.disputesInfo
  const hasDueInvoices = dueInvoices && dueInvoices.some((invoice) => invoice.entityType === 'ConsumablesEntity')
  const hasDisputeInvoices =
    disputeInvoices && disputeInvoices.some((invoice) => invoice.entityType === 'ConsumablesEntity')
  return hasDueInvoices || hasDisputeInvoices
}
